import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, Label, Textarea, selectClass } from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import { clearAnswerRating, rateAnswer } from "@/lib/api/chat";
import { qk } from "@/lib/query/keys";
import { cn } from "@/lib/utils";
import type { AnswerRating, ReasonCategory } from "@/types/api";

/** Thumbs up / down on an agent answer (AVI-171). */

export const REASON_LABEL: Record<ReasonCategory, string> = {
  wrong_data: "Wrong data",
  incomplete: "Incomplete",
  off_topic: "Off-topic",
  unsafe: "Unsafe",
  other: "Other",
};

const REASONS = Object.keys(REASON_LABEL) as ReasonCategory[];

export function AnswerRatingControls({
  conversationId,
  messageId,
  current,
}: {
  conversationId: string;
  messageId: number;
  current: AnswerRating | null | undefined;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [category, setCategory] = React.useState<ReasonCategory | "">("");
  const [reason, setReason] = React.useState("");

  const refresh = () => qc.invalidateQueries({ queryKey: qk.chatConversation(conversationId) });

  const rate = useMutation({
    mutationFn: (body: Parameters<typeof rateAnswer>[2]) =>
      rateAnswer(conversationId, messageId, body),
    onSuccess: async (r) => {
      toast.success(r.rating === "up" ? "Marked as a good answer." : "Thanks — reason saved.");
      setOpen(false);
      await refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const clear = useMutation({
    mutationFn: () => clearAnswerRating(conversationId, messageId),
    onSuccess: refresh,
    onError: (e: Error) => toast.error(e.message),
  });

  const isUp = current?.rating === "up";
  const isDown = current?.rating === "down";
  const busy = rate.isPending || clear.isPending;

  return (
    <>
      <button
        type="button"
        aria-label={isUp ? "Remove good rating" : "Good answer"}
        aria-pressed={isUp}
        disabled={busy}
        className={cn(
          "rounded p-1 transition-colors hover:text-foreground",
          isUp ? "text-success" : "text-muted-foreground",
        )}
        onClick={() => (isUp ? clear.mutate() : rate.mutate({ rating: "up" }))}
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        aria-label={isDown ? "Change bad rating" : "Bad answer"}
        aria-pressed={isDown}
        disabled={busy}
        className={cn(
          "rounded p-1 transition-colors hover:text-foreground",
          isDown ? "text-destructive" : "text-muted-foreground",
        )}
        onClick={() => {
          setCategory(current?.category ?? "");
          setReason(current?.reason ?? "");
          setOpen(true);
        }}
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </button>
      {isDown && current?.category ? (
        <span className="text-xs text-muted-foreground">{REASON_LABEL[current.category]}</span>
      ) : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>What was wrong with this answer?</DialogTitle>
            <DialogDescription>
              Your reason goes to the agent&apos;s engineers so they can fix it.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (category && reason.trim().length >= 3) {
                rate.mutate({ rating: "down", category, reason: reason.trim() });
              }
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor={`why-${messageId}`}>Problem</Label>
              <select
                id={`why-${messageId}`}
                className={selectClass}
                value={category}
                onChange={(e) => setCategory(e.target.value as ReasonCategory)}
              >
                <option value="" disabled>
                  Choose one…
                </option>
                {REASONS.map((r) => (
                  <option key={r} value={r}>
                    {REASON_LABEL[r]}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`reason-${messageId}`}>Reason</Label>
              <Textarea
                id={`reason-${messageId}`}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="e.g. The count is 40, not 42 — it included cancelled sign-ups."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!category || reason.trim().length < 3 || rate.isPending}
              >
                {rate.isPending ? "Saving…" : "Rate as bad"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
